import React, { useState } from "react";
import { Plus, CreditCard } from "lucide-react";
import { AddMembershipPlanModal, MembershipPlanFormInput } from "../../components/sunbeds/AddMembershipPlanModal";
import { DeleteConfirmationModal } from "../../components/sunbeds/DeleteConfirmationModal";
import { Button } from "../../components/ui/Button";
import {
  ApiError,
  createSunbedMembershipPlan,
  deleteSunbedMembershipPlan,
  getSunbedMembershipPlans,
  SunbedMembershipPlanApi,
  updateSunbedMembershipPlan,
} from "../../services/sunbeds";
import { useToast } from "../../hooks/useToast";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { SunbedActionToolbar } from "../../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import { MembershipPlansTable } from "../../components/sunbeds/MembershipPlansTable";

const SunbedMembershipPlans: React.FC = () => {
  const showToast = useToast();
  const [plans, setPlans] = useState<SunbedMembershipPlanApi[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SunbedMembershipPlanApi | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const formatSaveError = (error: unknown): string => {
    const apiErr = error as ApiError;
    if (apiErr?.type === "serverValidation" && apiErr.errors) {
      return Object.values(apiErr.errors).join(" ");
    }
    if (apiErr?.type === "network" || apiErr?.type === "server") {
      return apiErr.message || "Could not save membership plan.";
    }
    if (error instanceof Error && error.message) return error.message;
    return "Could not save membership plan.";
  };

  const fetchPlans = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getSunbedMembershipPlans();
      setPlans(res.data || []);
    } catch (error) {
      console.error("Error loading membership plans:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const filteredPlans = plans.filter((plan) =>
    plan.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlans = filteredPlans.slice(indexOfFirstItem, indexOfLastItem);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleFormSubmit = async (formData: MembershipPlanFormInput) => {
    const payload = {
      name: formData.name,
      description: formData.description || "",
      status: formData.status,
      interval: formData.interval,
      interval_count: formData.interval_count,
      price: formData.price,
      entitlement: formData.entitlement,
      minutes_per_period: formData.minutes_per_period,
      rollover: formData.rollover,
      max_session_minutes: formData.max_session_minutes,
      room_ids: formData.room_ids,
    };

    try {
      if (modalMode === "add") {
        await createSunbedMembershipPlan(payload);
      } else if (editingPlan?.id) {
        await updateSunbedMembershipPlan(editingPlan.id, payload);
      }
      await fetchPlans();
      setIsModalOpen(false);
      showToast.success(
        modalMode === "add" ? "Membership plan created." : "Membership plan updated."
      );
    } catch (error: unknown) {
      showToast.error(formatSaveError(error));
      throw error;
    }
  };

  const executeDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSunbedMembershipPlan(deleteId);
      await fetchPlans();
      setDeleteId(null);
    } catch (error: any) {
      showToast.error(error.message || "Could not delete plan.");
    } finally {
      setIsDeleteOpen(false);
    }
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
        <SubPageHeader
          title="Membership plans"
          subtitle="Recurring sunbed memberships sold at POS or assigned to customers"
          icon={CreditCard}
          actions={
            <Button
              onClick={() => {
                setModalMode("add");
                setEditingPlan(null);
                setIsModalOpen(true);
              }}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-5 py-2.5"
            >
              <Plus size={18} />
              Create plan
            </Button>
          }
        />

        <SunbedActionToolbar
          totalItems={plans.length}
          showingStart={currentPlans.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredPlans.length)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search plans..."
        />

        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <MembershipPlansTable
            plans={currentPlans}
            isLoading={isLoading}
            indexOfFirstItem={indexOfFirstItem}
            onEdit={(plan) => {
              setModalMode("edit");
              setEditingPlan(plan);
              setIsModalOpen(true);
            }}
            onDelete={(id) => {
              setDeleteId(id);
              setIsDeleteOpen(true);
            }}
          />
          <PaginationFooter
            totalItems={plans.length}
            filteredTotal={filteredPlans.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <AddMembershipPlanModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleFormSubmit}
        mode={modalMode}
        existingPlan={editingPlan}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={executeDelete}
        title="Delete membership plan"
        message="Are you sure you want to delete this plan? Existing customer memberships are not removed."
      />
    </div>
  );
};

export default SunbedMembershipPlans;
